import { Injectable } from '@angular/core';
import { PaginationState } from '@shared/models/pagination-state';
import { Sorting } from '@shared/models/sorting';
import { User } from '@shared/models/user';
import { UserFilters } from '@shared/models/user-filters';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { Session } from '@shared/models/session';

@Injectable({
  providedIn: 'root',
})
export class UsersApiService {
  constructor(private apiService: ApiService) {}

  show(user_id: string): Observable<{ data: User }> {
    return this.apiService.get(`v1/users/${user_id}`, {});
  }

  index(
    paginationState: PaginationState,
    filters: UserFilters,
    sorting: Sorting,
  ): Observable<{ data: User[]; meta: PaginationState }> {
    let params = {
      ...filters,
      page: paginationState.page,
      per_page: paginationState.per_page,
      sort: `${sorting.key}`,
      sort_direction: `${sorting.direction}`,
    };
    return this.apiService.get('v1/users', params);
  }

  create(user: Partial<User>): Observable<{ data: User }> {
    return this.apiService.post(`v1/users`, { data: user });
  }

  update(user: Partial<User>): Observable<{ data: User }> {
    return this.apiService.put(`v1/users/${user.id}`, { data: user });
  }

  session(): Observable<{ data: User; config: any }> {
    return this.apiService.get('v1/users/session', {});
  }

  endSession(): Observable<{}> {
    return this.apiService.delete('v1/users/end_session', {});
  }

  resetPassword(user_id: string): Observable<{}> {
    return this.apiService.post(`v1/users/${user_id}/send_forgot_password`, {});
  }

  resendAccountActivation(user_id: string): Observable<{}> {
    return this.apiService.post(`v1/users/${user_id}/send_activation_email`, {});
  }

  resendEmailConfirmation(user_id: string): Observable<{}> {
    return this.apiService.post(`v1/users/${user_id}/send_email_confirmation`, {});
  }

  sendMfaRecoveryLink(user_id: string, otp: string): Observable<{}> {
    return this.apiService.post(`v1/users/${user_id}/send_mfa_recovery_link`, { data: { otp: otp } });
  }

  disableMfa(user_id: string, otp?: string, recovery_code?: string): Observable<{}> {
    return this.apiService.delete(`v1/users/${user_id}/disable_mfa`, {
      data: { otp: otp, recovery_code: recovery_code },
    });
  }

  enableMfa(user_id: string, otp: string): Observable<{ data: { recovery_codes: string[] } }> {
    return this.apiService.post(`v1/users/${user_id}/enable_mfa`, { data: { otp: otp } });
  }

  mfaSetup(user_id: string): Observable<{ data: { qr_code: string; secret_key: string } }> {
    return this.apiService.get(`v1/users/${user_id}/mfa_setup`, {});
  }

  mfaRecoveryCodes(user_id: string): Observable<{ data: { recovery_codes: string[] } }> {
    return this.apiService.get(`v1/users/${user_id}/mfa_recovery_codes`, {});
  }
}
